import React, { useState, useEffect } from "react";
import SEO from "../seo";
const BrandIdentity = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "store-name",
        "logo",
        "favicon",
        "brand-colors",
        "social-media-links",
        "featured-brand",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Brand Identity | xPage Help Center"
        description="Learn how to configure your Brand Identity in xPage, including store name, logo, favicon, brand colors, social media links, and featured brands to build a consistent and professional brand presence."
        keywords="xPage, brand identity, logo upload, favicon, brand colors, store name, social media links, featured brand, branding settings, customize brand"
        canonical="https://help.xpage.ai/help/settings/Brand%20Identity"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Brand Identity
          </h1>
          <p className="text-sm text-systemLabel">
            The Brand Identity section in Xpage is your central hub for defining
            how your store looks, feels, and communicates with your audience. A
            consistent and polished brand identity is essential for building
            trust, increasing recognition, and enhancing conversions across your
            landing pages and storefront.
          </p>
          <p className="text-sm text-systemLabel">
            This section gives you full control over visual elements, colors,
            logos, and featured brands—all designed to reflect your unique brand
            personality.
          </p>
        </header>

        {/* Store Name Section */}
        <section id="store-name" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Store Name
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Store Name
                </h3>
                <p className="text-sm text-systemLabel">
                  Your Store Name serves as the foundation of your brand
                  identity.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Automatic Logo Generation
                </h3>
                <p className="text-sm text-systemLabel">
                  When you enter your store name, Xpage automatically generates
                  a default logo, ensuring your store has an immediate,
                  professional visual presence.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Flexible Updates
                </h3>
                <p className="text-sm text-systemLabel">
                  You can update or change the store name at any time, and all
                  associated brand elements will adjust accordingly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Logo Section */}
        <section id="logo" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">Logo</h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Custom Logo Upload
                </h3>
                <p className="text-sm text-systemLabel">
                  Upload a custom logo to replace the automatically generated
                  version.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Brand Recognition
                </h3>
                <p className="text-sm text-systemLabel">
                  Your logo is the cornerstone of brand recognition, appearing
                  prominently across your landing pages, checkout, and marketing
                  materials.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Quality Guidelines
                </h3>
                <p className="text-sm text-systemLabel">
                  Ensure your logo is high-quality, aligned with your brand
                  colors, and visually consistent to maximize impact.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Favicon Section */}
        <section id="favicon" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Favicon
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Favicon Upload
                </h3>
                <p className="text-sm text-systemLabel">
                  Upload a favicon to appear in browser tabs, bookmarks, and
                  shared links.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Professional Appearance
                </h3>
                <p className="text-sm text-systemLabel">
                  A professional favicon strengthens brand recall and gives your
                  store a polished, trustworthy appearance to visitors.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Colors Section */}
        <section id="brand-colors" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Brand Colors
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Color Definition
                </h3>
                <p className="text-sm text-systemLabel">
                  Define your Primary and Secondary brand colors to create a
                  cohesive visual language.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Visual Application
                </h3>
                <p className="text-sm text-systemLabel">
                  Colors are applied across landing pages, buttons, banners, and
                  call-to-action elements to reinforce your brand's identity.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Strategic Benefits
                </h3>
                <p className="text-sm text-systemLabel">
                  Thoughtful color selection enhances readability,
                  accessibility, and overall aesthetics while boosting customer
                  engagement and conversion rates.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Social Media Links Section */}
        <section id="social-media-links" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Social Media Links
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Profile Integration
                </h3>
                <p className="text-sm text-systemLabel">
                  Add links to your social media profiles to seamlessly
                  integrate your online presence with your store.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Cross-Platform Benefits
                </h3>
                <p className="text-sm text-systemLabel">
                  By connecting your store with social platforms, you can expand
                  reach, drive traffic, and create a consistent brand experience
                  across multiple channels.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Visibility
                </h3>
                <p className="text-sm text-systemLabel">
                  These links can appear on landing pages, footers, and
                  marketing materials to encourage engagement and loyalty.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Brand Section */}
        <section id="featured-brand" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Featured Brand
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Automatic Display
                </h3>
                <p className="text-sm text-systemLabel">
                  Featured brands are automatically generated and displayed on
                  your landing pages to highlight key products or partnerships.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Customization Control
                </h3>
                <p className="text-sm text-systemLabel">
                  You have full control to customize, replace, or update
                  featured brands to align with promotions, seasonal campaigns,
                  or strategic product highlights.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Strategic Impact
                </h3>
                <p className="text-sm text-systemLabel">
                  Optimizing featured brands can enhance credibility, guide
                  customer attention, and increase conversion potential on your
                  landing pages.
                </p>
              </div>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                By leveraging the Brand Identity section effectively, you ensure
                that every visitor experiences a consistent, professional, and
                engaging brand presence. This foundation not only strengthens
                recognition but also builds trust, encourages conversions, and
                positions your store for long-term growth.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#store-name"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "store-name"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Store Name
              </a>
            </li>
            <li>
              <a
                href="#logo"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "logo"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Logo
              </a>
            </li>
            <li>
              <a
                href="#favicon"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "favicon"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Favicon
              </a>
            </li>
            <li>
              <a
                href="#brand-colors"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "brand-colors"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Brand Colors
              </a>
            </li>
            <li>
              <a
                href="#social-media-links"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "social-media-links"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Social Media Links
              </a>
            </li>
            <li>
              <a
                href="#featured-brand"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "featured-brand"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Featured Brand
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BrandIdentity;
